import { Component, ElementRef, OnInit, ViewChild, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MultiDropboxData, MultiDropboxComponent } from '../shared';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormControl, FormGroupDirective, NgForm, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { SearchFilterAggregate } from '../search-filters/search-filters.service';
import { ProfileModelService } from '../profile/profile-model.service';
import { ConfirmationDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';

export interface Keyword {
    name: string;
}

export interface DialogData {
    filterName: string;
    stations: MultiDropboxData[];
    markets: MultiDropboxData[];
    formats?: MultiDropboxData[];
    keywords?: any;
    origMarketsList: any;
    origStationsList: any;
    origFormatsList?: any;
    oldFilterName?: string;
    isDelete?: boolean;
    isHomePage?: boolean;
}

class CustomValidator {
    static uniqueFilterNameValidator(profileModelService: ProfileModelService, isHomePage: boolean): ValidatorFn {

        return (control: AbstractControl): { [key: string]: boolean } | null => {
            if (control.pristine) {
                return null;
            }
            control.markAsTouched();
            let filterNameCheck = false;
            if (isHomePage) {
                filterNameCheck = profileModelService.findStationFilterByName(control.value) != null;
            } else {
                filterNameCheck = profileModelService.findSearchFilterByName(control.value) != null;
            }
            if (!filterNameCheck) {
                return null;
            }
            return {
                invalidFilterName: true
            };
        };
    }
}

export class FormFieldErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}


@Component({
    selector: 'app-search-dialog',
    templateUrl: 'search-dialog.component.html',
    styleUrls: ['search-dialog.component.scss']
})
export class SearchDialogComponent implements OnInit {

    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    selectable = true;
    addOnBlur = true;
    filterSearchNameError = false;
    isEditMode = false;
    oldFilterName: string;
    filterSearchErrorMessage = 'Filter Name was not provided.';
    saveButtonText = 'SAVE';
    deleteButtonText = 'DELETE';

    dialogTitle = 'Manage Transcription Filter';


    keywords: Keyword[] = [];
    markets: MultiDropboxData[] = [];
    stations: MultiDropboxData[] = [];
    formats: MultiDropboxData[] = [];

    isHomePage = 'false';

    @ViewChild('keywordChipInputBox',{static: true}) keywordChipInputBox: ElementRef<HTMLInputElement>;
    @ViewChild('stationMulti',{static: true}) stationMulti: MultiDropboxComponent;
    @ViewChild('marketMulti',{static: true}) marketMulti: MultiDropboxComponent;
    @ViewChild('formatMulti',{static: true}) formatMulti: MultiDropboxComponent;

    filterNameFormControl: FormControl;
    filterNameMatcher: FormFieldErrorStateMatcher;
    disableBtn = true;

    constructor(
        public dialog: MatDialog, private dialogRef: MatDialogRef<SearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData,
        private profileModelService: ProfileModelService
    ) {
        this.keywords = data.keywords ? [].concat(data.keywords) :  [];
        this.markets = data.markets ? [].concat(data.markets) :  [];
        this.stations = data.stations ? [].concat(data.stations) :  [];
        this.formats = data.formats ? [].concat(data.formats) :  [];
        if (data.oldFilterName) {
            this.isEditMode = true;
            this.oldFilterName = data.oldFilterName;
            this.saveButtonText = 'UPDATE';
        } else {
            this.saveButtonText = 'SAVE';
            this.deleteButtonText = 'DELETE';
        }
        this.isHomePage = data.isHomePage ? (data.isHomePage + '') : 'false';
        if (this.isHomePage === 'true') {
            this.dialogTitle = 'Manage Station Filter';
        } else {
            data.formats = [];
        }

        this.filterNameFormControl = new FormControl('', [
            Validators.required, CustomValidator.uniqueFilterNameValidator(this.profileModelService, this.isHomePage === 'true')
        ]);

        this.filterNameMatcher = new FormFieldErrorStateMatcher();

        this.filterNameFormControl.valueChanges.subscribe((changedObj: any) => {
            this.disableBtn = !this.filterNameFormControl.valid;
        });

        this.filterNameFormControl.setValue(data.filterName || '');
    }

    ngOnInit() {
        this.marketMulti.setList(this.data.origMarketsList.map((marketFilter: SearchFilterAggregate) => {
            return {
                id: marketFilter.aggregate,
                name: marketFilter.name
            };
        }));

        this.loadStations();

        if (this.isHomePage === 'true') {
            this.loadFormats();
        }
    }

    clear() {
        this.keywords = [];
        this.stations = [];
        this.markets = [];
        this.formats = [];
    }

    onNoClick(): void {
        const dialogConfirmRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '400px',
            data: {
                'text': 'Do you want to leave without saving your filter?'
            }
        });
        dialogConfirmRef.afterClosed().subscribe(result => {
            if (!!result) {
                this.dialogRef.close();
            }
        });
    }

    saveStationFilter(): void {
        if (this.isEditMode) {
            this.profileModelService.deleteStationFilterByName(this.oldFilterName);
        }
        const filterNameExists = this.profileModelService.findStationFilterByName(this.filterNameFormControl.value);
        if (!this.filterNameFormControl.invalid && !filterNameExists) {
            this.filterSearchNameError = false;
            this.dialogRef.close({
                'filterName': this.filterNameFormControl.value,
                'oldFilterName': this.oldFilterName,
                'markets': this.markets,
                'stations': this.stations,
                'formats': this.formats
            });
        }
    }

    deleteStationFilter(): void {
        if (this.isEditMode) {
            const dialogConfirmRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '350px',
                data: {
                    'text': 'Do you want to delete this filter?'
                }
            });
            dialogConfirmRef.afterClosed().subscribe(result => {
                if (!!result) {
                    this.profileModelService.deleteStationFilterByName(this.oldFilterName);
                    this.dialogRef.close({
                        'isDelete': true
                    });
                }
            });
        }
    }

    saveTranscriptFilter(): void {
        if (this.isEditMode) {
            this.profileModelService.deleteSearchFilterByName(this.oldFilterName);
        }
        const filterNameExists = this.profileModelService.findSearchFilterByName(this.filterNameFormControl.value);
        if (!this.filterNameFormControl.invalid && !filterNameExists) {
            this.filterSearchNameError = false;
            this.dialogRef.close({
                'filterName': this.filterNameFormControl.value,
                'oldFilterName': this.oldFilterName,
                'markets': this.markets,
                'stations': this.stations,
                'keywords': this.keywords
            } as DialogData);
        }
    }

    deleteTranscriptFilter(): void {
        if (this.isEditMode) {
            const dialogConfirmRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '350px',
                data: {
                    'text': 'Do you want to delete this filter?'
                }
            });
            dialogConfirmRef.afterClosed().subscribe(result => {
                if (!!result) {
                    this.profileModelService.deleteSearchFilterByName(this.oldFilterName);
                    this.dialogRef.close({
                        'isDelete': true
                    });
                }
            });
        }
    }

    addKeyword(event: MatChipInputEvent): void {
        const input = event.input;
        const value = event.value;

        if ((value || '').replace(/"/g, '').trim()) {
            this.pushKeywordList(value);
        }

        if (input) {
            input.value = '';
        }
    }

    private pushKeywordList(keywordString: string) {
        keywordString.match(/\w+|"[^"]+"/g).map(key => {
            const isKeyExist = this.keywords.filter(keyword => (keyword.name === key)).length > 0;
            if (!isKeyExist) {
                this.keywords.push({ 'name': key });
            }
        });
    }

    removeKeyword(keyword: Keyword): void {
        const index = this.keywords.indexOf(keyword);

        if (index >= 0) {
            this.keywords.splice(index, 1);
        }

        this.keywordChipInputBox.nativeElement.focus();
    }

    removeMarket(market: MultiDropboxData) {
        this.markets = this.markets.filter(item => item !== market);
        this.formats = this.formats.filter(item => item.model.aggregate.filter(
            formatMarkets => new Set(this.markets.reduce(
                (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
            )).has(formatMarkets)).length > 0);
        this.stations = this.stations.filter(item => !new Set(market.id).has(item.model[0].market));

        this.loadFormats();
        this.loadStations();
    }

    removeStation(station: MultiDropboxData) {
        this.stations = this.stations.filter(item => item !== station);
    }

    removeFormat(format: MultiDropboxData) {
        this.formats = this.formats.filter(item => item !== format);
        this.stations = this.stations.filter(item => item.model[0].format !== format.id);

        this.loadStations();
    }

    loadStations() {
        let filterStations = [].concat(this.data.origStationsList || []);
        if (this.markets != null && this.markets.length > 0) {
            filterStations = filterStations.filter((station) => this.markets.reduce(
                (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
            ).includes(station.market));
        }

        if (this.formats != null && this.formats.length > 0) {
            filterStations = filterStations.filter((station) => this.formats.map(format => format.id).includes(station.format));
        }

        this.stationMulti.setList(filterStations.map((station) => {
            const name = station.name ? station.name : '';
            const frequency = station.frequency ? station.frequency : '';

            return {
                id: station.id,
                name: `${name} ${frequency}`,
                model: station
            };
        }));
    }

    loadFormats() {
        const formats = [].concat(this.data.origFormatsList || []);
        const filterFormats = formats.reduce((combiner, transformer) => {
            if (
                this.markets == null
                || this.markets.length <= 0
                || transformer.aggregate.filter(market => this.markets.reduce(
                    (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
                ).includes(market)).length > 0
            ) {
                combiner.push(transformer);
            }
            return combiner;
        }, []);

        this.formatMulti.setList(filterFormats.map((format) => {
            return {
                id: String(format.name),
                name: String(format.name),
                model: format
            };
        }));
    }

}
